import React, { useMemo } from 'react';
import { Table } from 'reactstrap';
import { ColumnSpec } from '~types/columnSpec';

import { formatCurrency } from '~libs/dataUtils';
import dayjs, { dateDisplayFormat, dateInputFieldFormat } from '~libs/dayjs';

import TableEmpty from '~components/table-view/TableEmpty';
import TableLoading from '~components/table-view/TableLoading';

import { StatementBudgetsSummaryRow } from '~support-at-home/models/dashboard';

interface ComponentProps {
  rows: StatementBudgetsSummaryRow[];
  isLoading: boolean;
  visibleFundingTypes?: string[] | null;
  /**
   * Visible column specs from the column picker. Controls the identifier columns only (care
   * manager, care recipient ID, period), headed with the spec label so the table, the picker and
   * the CSV use the same names; funding type groups follow visibleFundingTypes.
   * Null (specs not loaded) hides the optional columns.
   */
  visibleColumns?: Pick<ColumnSpec, 'key' | 'label'>[] | null;
}

const OPTIONAL_COLUMN_KEYS = ['care_recipient_id', 'care_manager_name', 'statement_period'] as const;

type OptionalColumnKey = (typeof OPTIONAL_COLUMN_KEYS)[number];

// Statement dates are DATE columns serialised as midnight UTC; read the date part only so the
// day cannot shift with the viewer's timezone
const formatStatementDate = (value: string) =>
  dayjs(value.slice(0, 10), dateInputFieldFormat).format(dateDisplayFormat);

const formatPeriod = ({ start, end }: StatementBudgetsSummaryRow['statement_period']) =>
  `${formatStatementDate(start)} - ${formatStatementDate(end)}`;

const renderOptionalCell = (key: OptionalColumnKey, row: StatementBudgetsSummaryRow) => {
  if (key === 'statement_period') return formatPeriod(row.statement_period);
  return row[key] ?? '--';
};

const StatementBudgetsTable: React.FC<ComponentProps> = ({ rows, isLoading, visibleFundingTypes, visibleColumns }) => {
  const optionalColumns = useMemo(
    () =>
      OPTIONAL_COLUMN_KEYS.flatMap((key) => {
        const spec = visibleColumns?.find((column) => column.key === key);
        return spec ? [{ key, header: spec.label }] : [];
      }),
    [visibleColumns],
  );

  const budgetTypeLabels = useMemo(() => {
    if (visibleFundingTypes && visibleFundingTypes.length > 0) {
      return [...visibleFundingTypes].sort();
    }
    // Fallback: derive from current page rows (used when summary data is unavailable)
    const labels = new Set<string>();
    rows.forEach((row) => {
      row.budgets.forEach((budget) => {
        if (budget.budget_type_label) labels.add(budget.budget_type_label);
      });
    });
    return Array.from(labels).sort();
  }, [rows, visibleFundingTypes]);

  // 1 (Client) + optional identifiers + 1 (Statement) + 3 per budget type + 1 (Co-Contributions)
  const totalColSpan = 2 + optionalColumns.length + budgetTypeLabels.length * 3 + 1;

  return (
    <div className="table-view statement-budget-table">
      <Table size="sm" responsive striped bordered data-cy="statement-budget-table">
        <thead>
          <tr>
            <th rowSpan={2} className="align-middle">
              Client
            </th>
            {optionalColumns.map(({ key, header }) => (
              <th key={key} rowSpan={2} className="align-middle">
                {header}
              </th>
            ))}
            <th rowSpan={2} className="align-middle">
              Statement ID
            </th>
            {budgetTypeLabels.map((label) => (
              <th key={label} colSpan={3} className="text-center group-border">
                {label}
              </th>
            ))}
            <th rowSpan={2} className="text-center align-middle group-border">
              Co-Contributions
            </th>
          </tr>
          <tr>
            {budgetTypeLabels.map((label) => (
              <React.Fragment key={label}>
                <th className="text-end group-border">Opening</th>
                <th className="text-end">Spent</th>
                <th className="text-end">Closing</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading colSpan={totalColSpan} />}
          {!isLoading && rows.length === 0 && (
            <TableEmpty colSpan={totalColSpan} message="No statement data found for the selected filters" />
          )}
          {!isLoading &&
            rows.map((row) => (
              <tr key={row.statement_id} data-cy={`statement-budget-row-${row.statement_id}`}>
                <td>{row.client_name}</td>
                {optionalColumns.map(({ key }) => (
                  <td key={key}>{renderOptionalCell(key, row)}</td>
                ))}
                <td>{row.statement_reference}</td>
                {budgetTypeLabels.map((label) => {
                  const entry = row.budgets.find((b) => b.budget_type_label === label);
                  return (
                    <React.Fragment key={label}>
                      <td className="text-end group-border">{entry ? formatCurrency(entry.opening_balance) : '--'}</td>
                      <td className="text-end">{entry ? formatCurrency(entry.spent) : '--'}</td>
                      <td className="text-end">{entry ? formatCurrency(entry.closing_balance) : '--'}</td>
                    </React.Fragment>
                  );
                })}
                <td className="text-end group-border">{formatCurrency(row.co_contributions)}</td>
              </tr>
            ))}
        </tbody>
      </Table>
    </div>
  );
};

export default StatementBudgetsTable;
